import { EntityType } from 'api'
import { useCallback } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { createSelectorCreator, lruMemoize, Selector, SHOULD_AUTOBATCH, ThunkAction, UnknownAction } from '@reduxjs/toolkit'
import { GlobalState } from 'store'
import { AnyFunction } from './types'
import { Dependencies } from 'epics'
import { createStore } from 'createStore'
import { isEqual } from 'lodash-es'

export interface ActionRepositoryMeta {
  entityIds: string[]
  entityType: EntityType
}

export enum ResponseType {
  NONE = 'NONE',
  DIALOG = 'DIALOG',
  GROWL = 'GROWL'
}

export interface ActionResponseMeta {
  errorResponse?: ResponseType
  errorMessage?: string
  errorRetry?: boolean
  successResponse?: ResponseType
  successMessage?: string
}

export interface ActionMeta {
  [SHOULD_AUTOBATCH]?: boolean
  sourceWindowId?: string
  sendToServer?: boolean
  ownOrder?: boolean
  persist?: boolean
  responseMeta?: ActionResponseMeta
  repositoryMeta?: ActionRepositoryMeta
}

export type Action<T extends string = string, P = void> = P extends void
  ? Readonly<{ type: T; meta?: ActionMeta }>
  : Readonly<{ type: T; payload: P; meta: ActionMeta }>

export type ActionsUnion<A extends Record<string, AnyFunction>> = ReturnType<A[keyof A]>

export function createAction<T extends string>(type: T): Action<T>
export function createAction<T extends string, P>(type: T, payload: P, meta?: ActionMeta): Action<T, P>
export function createAction<T extends string, P>(type: T, payload?: P, meta?: ActionMeta) {
  return payload === undefined ? { type, meta } : { type, payload, meta }
}

/** hook that allows to use parametric selectors
 * first arg is the reference to the selector, second arg are props
 **/
export const useParametricSelector = <TProps, TResult>(selector: ParametricSelector<GlobalState, TProps, TResult>, props: TProps) => {
  const callback = useCallback((state: GlobalState) => selector(state, props), [props, selector])
  return useSelector(callback)
}

/** hook factory for creating hooks with parametric selectors,
 * it will return a hook that as argument it needs only the defined parametric props,
 * instead of the two args as in useParametricSelector(selector, props)
 **/
export const createParametricSelectorHook = <TProps, TResult>(selector: ParametricSelector<GlobalState, TProps, TResult>) => {
  return (props: TProps) => {
    return useSelector((state: GlobalState) => selector(state, props))
  }
}

export const identityPropsSelector = (_: GlobalState, value: string) => value

export type DeepPartial<T> = {
  [K in keyof T]?: T[K] extends object ? DeepPartial<T[K]> : T[K]
}

/** @deprecated
 * reselect removed it on v5 but we have it all around,
 * we should try to move away from it and use the Selector type directly. */
export type ParametricSelector<State, Props, Result> = Selector<State, Result, [Props, ...any]>

export type AppThunk<ReturnType = void> = ThunkAction<ReturnType, GlobalState, Dependencies, UnknownAction>

type AppStore = ReturnType<typeof createStore>

/** Pre-Typed useSelector hook for our redux state */
export const useAppSelector = useSelector.withTypes<AppStore['getState']>()

/** Pre-Typed useDispatch hook, we need it to dispatch thunks
 *  as the regular useDispatch it's not taking into account any middleware.
 */
export const useAppDispatch = useDispatch.withTypes<AppStore['dispatch']>()

/**
 * To use only on selectors that causes big re-renders on screen,
 * using it could have a performance impact that we have to measure
 */
export const createDeepEqualSelector = createSelectorCreator(lruMemoize, { resultEqualityCheck: isEqual })

export const createShallowEqualSelector = createSelectorCreator(lruMemoize, { resultEqualityCheck: shallowEqual })

/**
 * Trick to debug selectors, change createSelector with createDebugSelector
 * and it will log whenever a selector is recomputed and what caused the recomputation.
 * It checks what input selector caused the recomputation of the selector.
 */
export const createDebugSelector = createSelectorCreator(lruMemoize, {
  equalityCheck: (previousVal, currentVal) => {
    const rv = currentVal === previousVal
    if (!rv) console.log('Selector param value changed', currentVal)
    return rv
  }
})
